import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PriorityBadge } from "../components/Badges";
import { Badge } from "../components/ui/badge";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import Attachments from "../components/Attachments";

const STATUSES = ["open", "in_progress", "waiting", "resolved", "closed"];
const empty = { title: "", client_name: "", project_id: "", priority: "medium", status: "open", assigned_to: "", description: "", start_date: "", end_date: "" };

const stColor = {
  open: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  in_progress: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  waiting: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  resolved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  closed: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

export default function TicketsPage() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/tickets").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const submit = async () => {
    try {
      if (editId) await api.patch(`/tickets/${editId}`, form);
      else await api.post("/tickets", form);
      setOpen(false); load(); toast.success("Saved");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="tickets-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Service</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Service Tickets</h1>
        </div>
        <Button onClick={() => { setEditId(null); setForm(empty); setOpen(true); }} data-testid="new-ticket-btn"><Plus className="h-4 w-4 mr-2" />New Ticket</Button>
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Title</TableHead><TableHead>Client</TableHead><TableHead>Assigned</TableHead>
            <TableHead>Priority</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((t) => (
              <TableRow key={t.id} className="cursor-pointer" onClick={() => { setEditId(t.id); setForm({ ...t }); setOpen(true); }} data-testid={`ticket-row-${t.id}`}>
                <TableCell className="font-medium">{t.title}</TableCell>
                <TableCell>{t.client_name}</TableCell>
                <TableCell className="text-sm">{users.find((u) => u.id === t.assigned_to)?.name || "—"}</TableCell>
                <TableCell><PriorityBadge value={t.priority} /></TableCell>
                <TableCell><Badge className={`${stColor[t.status]} border capitalize`}>{t.status.replace("_", " ")}</Badge></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No tickets.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Ticket" : "New Ticket"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Input placeholder="Client name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Project" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.client_name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Assignee" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
              </Select>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Start Date</label>
                <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">End Date</label>
                <Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" />
              </div>
            </div>
            {editId && (
              <div className="pt-2 border-t border-border">
                <Attachments kind="ticket" parentId={editId} />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit}>Save</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
